import * as React from 'react';
import { ReactElement, ReactNode } from 'react';
import { Button } from 'primereact/button';
import { DeleteButton } from '../../../components/form/MultiInput/Buttons';
import { IntlShape, useIntl } from 'react-intl';
import { InputText } from 'primereact/inputtext';
import { Fieldset } from '../layout/Fieldset';
import { Actions, MultiInputControls } from '../../../components/form/MultiInput/MultiInputControls';
import { ExternalIdDTO, WorkPersonDTO, WorkPersonOtherDTO } from '../../../../api/core';
import { getDefaultExternalId } from '../../../../Settings/emptyModels';
import { InputGroup } from '../layout/InputGroup';
import { LabelledDropdown } from '../../../components/form/LabelledDropdown';
import { getVocabularyOptions } from '../../../../Vocabulary/util';
import { LabelledInputText } from '../../../components/form/LabelledInputText';
import { CheckPermissionFunction } from '../../../../Auth/components/PermissionGate';
import { ListedEntity } from '../LinkedPerson';

interface Props {
  listedPerson: ListedEntity;
  first: boolean;
  last: boolean;
  length: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  children?: React.ReactNode;
  addPerson: (event: React.MouseEvent<any>, person: WorkPersonDTO) => void;
  addPersonOther: (event: React.MouseEvent<any>, person: WorkPersonOtherDTO) => void;
  enableExternalIds: boolean;
  updateExternalIds?: (externalIds: ExternalIdDTO[]) => void;
  checkPermissionOther: CheckPermissionFunction;
}

export const LinkedPersonRow = (props: Props): ReactNode => {
  const intl: IntlShape = useIntl();
  const [expanded, _setExpanded] = React.useState(false);
  const externalIds: ExternalIdDTO[] = 'externalIds' in props.listedPerson.entity ? props.listedPerson.entity.externalIds : undefined;

  const setExpanded = (event: React.MouseEvent<any>): void => {
    event.preventDefault();
    _setExpanded((value: boolean): boolean => !value);
  };

  const moveUp = (event: React.MouseEvent<any>): void => {
    event.preventDefault();
    props.onMoveUp();
  };

  const moveDown = (event: React.MouseEvent<any>): void => {
    event.preventDefault();
    props.onMoveDown();
  };

  const deleteEntry = (event: React.MouseEvent<any>): void => {
    event.preventDefault();
    props.onDelete();
  };

  const internTabClassNames = ["w-5rem p-inputgroup-addon border-round-top border-noround-bottom border-1"];
  if(props.listedPerson.internal) {
    internTabClassNames.push('primary-tab');
  } else {
    internTabClassNames.push('border-gray-300');
  }

  return (
    <div key={props.listedPerson.entity.sequence} className={'flex flex-column'}>
      <div className={'p-inputgroup h-2rem flex justify-content-end border-noround-bottom'}>
        <span className="w-5rem p-inputgroup-addon border-round-top border-noround-bottom border-1 border-gray-300">
          {props.listedPerson.entity.sequence}.
        </span>
        <span className={internTabClassNames.join(' ')}>
          {intl.formatMessage({
            id: props.listedPerson.internal ? 'work.button.text.addPersonIntern' : 'work.button.text.addPersonExtern',
          })}
        </span>
        <InputText disabled={true} className={'border-none'} />
        <Button
          id={`add-intern-${props.listedPerson.entity.sequence}`}
          className={'border-noround-bottom border-round-top border-bottom-none border-primary-600'}
          icon={'pi pi-plus'}
          label={intl.formatMessage({ id: 'work.button.text.addPersonIntern' })}
          onClick={(event): void => props.addPerson(event, { sequence: props.listedPerson.entity.sequence })}
        />
        <Button
          id={`add-intern-${props.listedPerson.entity.sequence}`}
          className={'border-noround-bottom border-round-top border-bottom-none border-primary-600'}
          icon={'pi pi-plus'}
          label={intl.formatMessage({ id: 'work.button.text.addPersonExtern' })}
          onClick={(event): void => props.addPersonOther(event, { sequence: props.listedPerson.entity.sequence })}
        />
      </div>
      <div className={'p-inputgroup h-2rem flex justify-content-end border-noround'}>
        <span className=" w-full p-inputgroup-addon  border-noround"></span>
      </div>
      <div className={'p-inputgroup border-noround-top flex h-4rem'}>
        {props.children}
        {props.enableExternalIds && (
          <Button
            className={`w-2rem flex-grow-0 flex-shrink-0`}
            severity={'secondary'}
            disabled={props.listedPerson.internal}
            outlined={true}
            title={intl.formatMessage({ id: 'button.expand' })}
            icon={props.listedPerson.internal ? undefined : 'pi pi-bars'}
            badge={externalIds ? externalIds.length.toString(10) || '0' : undefined}
            pt={{
              icon: { style: { marginLeft: '8px' } },
              badge: { severity: 'secondary', style: { marginLeft: '-5px', marginTop: '-15px' } },
            }}
            onClick={setExpanded}
          ></Button>
        )}
        {(props.length || 0) > 1 &&
          <span className={'p-inputnumber-button-group flex flex-column'}>
            <Button
              icon={props.first ? '' : 'pi pi-sort-up'}
              severity={'secondary'}
              outlined={true}
              title={intl.formatMessage({ id: 'button.moveUp' })}
              style={{ height: '50%', borderRadius: '0' }}
              disabled={props.first}
              onClick={moveUp}
            ></Button>
            <Button
              icon={props.last ? '' : 'pi pi-sort-down'}
              severity={'secondary'}
              outlined={true}
              title={intl.formatMessage({ id: 'button.moveDown' })}
              style={{ height: '50%', borderRadius: '0' }}
              disabled={props.last}
              onClick={moveDown}
            ></Button>
          </span>
        }
        <DeleteButton className={'border-noround-top'} onClick={deleteEntry} />
      </div>
      {props.enableExternalIds &&
        !props.listedPerson.internal &&
        expanded &&
        props.checkPermissionOther('external_ids').canRead && (
          <div className={'w-full'}>
            <Fieldset className={'mx-5'}>
              <MultiInputControls<ExternalIdDTO>
                canEdit={props.checkPermissionOther('external_ids').canWrite}
                lines={externalIds || []}
                onChange={(change: ExternalIdDTO[]): void => props.updateExternalIds(change)}
                addNew={(rows: ExternalIdDTO[]): number => rows.push(getDefaultExternalId())}
                renderLines={(
                  externalIdDTO: ExternalIdDTO,
                  onLineChange: (newValues: ExternalIdDTO) => void,
                  actions: Actions,
                  idx: number
                ): ReactElement => (
                  <InputGroup
                    delete={{
                      action: actions.delete,
                      disabled: !props.checkPermissionOther('external_ids').canWrite,
                    }}
                  >
                    <LabelledDropdown
                      id={`externalId-${idx}-type`}
                      value={externalIdDTO.type}
                      className={'w-15rem'}
                      permissions={props.checkPermissionOther('external_ids.type')}
                      validation={props.listedPerson.validation.fields?.external_ids.children[idx]?.fields?.type}
                      onChange={(type: string): void => onLineChange({ ...externalIdDTO, type })}
                      options={getVocabularyOptions(intl.locale, 'external_person_id_list')}
                      label={'work.attr.externalId.attr.type'}
                    />
                    <LabelledInputText
                      id={`externalId-${idx}-id`}
                      value={externalIdDTO.externalId}
                      permissions={props.checkPermissionOther('external_ids.external_id')}
                      validation={props.listedPerson.validation.fields?.external_ids.children[idx]?.fields?.external_id}
                      disabled={!externalIdDTO.type}
                      onChange={(externalId: string): void => onLineChange({ ...externalIdDTO, externalId })}
                      label={'work.attr.externalId.attr.id'}
                    />
                  </InputGroup>
                )}
              />
            </Fieldset>
          </div>
        )}
    </div>
  );
};
